import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Globe, 
  Save, 
  Plus, 
  Trash2, 
  Edit2,
  Image,
  Type,
  MessageSquare,
  BarChart3,
  Phone,
  Mail,
  MapPin,
  Clock,
  Link,
  AlertCircle,
  Check,
  Menu,
  Upload,
  CreditCard,
  Shield,
  Lock,
  FileText,
  Map,
  Palette,
  Ticket,
  Smartphone,
  Tablet,
  Monitor,
  RefreshCw,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUpload from "../../components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';

const AdminWebsitePage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeSection, setActiveSection] = useState("header");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load website settings. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSection = async (section) => {
    setSaving({ ...saving, [section]: true });
    try {
      await axios.put(`${API}/admin/site-settings/${section}`, settings[section]);
      toast.success(`${section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} saved successfully!`);
    } catch (error) {
      console.error(`Error saving ${section}:`, error);
      toast.error(`Failed to save ${section.replace(/_/g, ' ')}. Please try again.`);
    } finally {
      setSaving({ ...saving, [section]: false });
    }
  };

  // Image upload handler
  const handleImageUpload = async (file, onSuccess) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, GIF, WEBP');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'gallery');
    
    try {
      const response = await axios.post(`${API}/admin/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onSuccess(response.data.url);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const updateSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const updateNestedSetting = (section, key, nestedKey, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: {
          ...prev[section][key],
          [nestedKey]: value
        }
      }
    }));
  };

  const sections = [
    { id: "preview", label: "📱 Responsive Preview", icon: Monitor },
    { id: "header", label: "Header & Logo", icon: AlertCircle },
    { id: "navigation", label: "Navigation Menu", icon: Menu },
    { id: "hero", label: "Homepage Hero", icon: Image },
    { id: "about_section", label: "About Section", icon: Type },
    { id: "featured_section", label: "Featured Section", icon: BarChart3 },
    { id: "stats", label: "Stats Section", icon: BarChart3 },
    { id: "testimonials_section", label: "Testimonials Header", icon: MessageSquare },
    { id: "testimonials", label: "Testimonials Content", icon: MessageSquare },
    { id: "cta_section", label: "CTA Section", icon: Type },
    { id: "about_page", label: "📄 About Us Page", icon: FileText },
    { id: "checkout", label: "Checkout Page", icon: CreditCard },
    { id: "ticket_colors", label: "Ticket Status Colors", icon: Ticket },
    { id: "contact_page", label: "Contact Page", icon: MapPin },
    { id: "privacy_policy", label: "Privacy Policy", icon: FileText },
    { id: "terms_of_service", label: "Terms of Service", icon: FileText },
    { id: "quick_links", label: "Quick Links", icon: Link },
    { id: "services", label: "Services", icon: Type },
    { id: "footer", label: "Footer", icon: Globe }
  ];

  // Responsive preview state
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewPage, setPreviewPage] = useState('/');
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef(null);

  const deviceSizes = {
    mobile: { width: 375, height: 667, label: 'Mobile', icon: Smartphone },
    tablet: { width: 768, height: 1024, label: 'Tablet', icon: Tablet },
    desktop: { width: 1280, height: 800, label: 'Desktop', icon: Monitor }
  };

  const previewPages = [
    { path: '/', label: 'Homepage' },
    { path: '/about', label: 'About Us' },
    { path: '/experiences', label: 'Experiences' },
    { path: '/events', label: 'Events' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/contact', label: 'Contact' },
    { path: '/booking', label: 'Booking' },
    { path: '/privacy', label: 'Privacy Policy' },
    { path: '/terms', label: 'Terms of Service' }
  ];

  const refreshPreview = () => {
    setPreviewKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-website">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
            Website Settings
          </h1>
          <p className="text-[#A1A1AA]">Edit your website content, banners, and footer</p>
        </div>

        {/* Section Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-[rgba(212,175,55,0.15)] pb-4">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 flex items-center gap-2 transition-colors ${
                activeSection === section.id
                  ? "bg-[#D4AF37] text-[#050505]"
                  : "bg-[#121212] text-[#A1A1AA] hover:text-[#F5F5F0]"
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </div>

        {/* Responsive Preview Section */}
        {activeSection === "preview" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Responsive Preview
              </h2>
              
              <div className="flex flex-wrap items-center gap-4">
                {/* Page Selector */}
                <select
                  value={previewPage}
                  onChange={(e) => setPreviewPage(e.target.value)}
                  className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] px-4 py-2 focus:border-[#D4AF37] focus:outline-none"
                >
                  {previewPages.map(page => (
                    <option key={page.path} value={page.path}>{page.label}</option>
                  ))}
                </select>

                {/* Device Buttons */}
                <div className="flex border border-[rgba(212,175,55,0.3)] rounded overflow-hidden">
                  {Object.entries(deviceSizes).map(([key, device]) => {
                    const IconComponent = device.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setPreviewDevice(key)}
                        className={`flex items-center gap-2 px-4 py-2 transition-colors ${
                          previewDevice === key 
                            ? 'bg-[#D4AF37] text-[#050505]' 
                            : 'bg-[#0F0F0F] text-[#A1A1AA] hover:text-[#D4AF37]'
                        }`}
                        title={`${device.label} (${device.width}x${device.height})`}
                      >
                        <IconComponent className="w-4 h-4" />
                        <span className="hidden sm:inline">{device.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Refresh Button */}
                <button
                  onClick={refreshPreview}
                  className="p-2 border border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                  title="Refresh Preview"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>

                {/* Open in New Tab */}
                <a
                  href={`${SITE_URL}${previewPage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                  title="Open in New Tab"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Device Info */}
            <div className="flex items-center justify-center gap-4 mb-4 text-sm text-[#A1A1AA]">
              <span className="flex items-center gap-2">
                {previewDevice === 'mobile' && <Smartphone className="w-4 h-4 text-[#D4AF37]" />}
                {previewDevice === 'tablet' && <Tablet className="w-4 h-4 text-[#D4AF37]" />}
                {previewDevice === 'desktop' && <Monitor className="w-4 h-4 text-[#D4AF37]" />}
                <span className="text-[#F5F5F0] font-medium">{deviceSizes[previewDevice].label}</span>
              </span>
              <span className="text-[#a1a1aa]">|</span>
              <span>{deviceSizes[previewDevice].width} × {deviceSizes[previewDevice].height}px</span>
              <span className="text-[#a1a1aa]">|</span>
              <span className="text-[#D4AF37]">{previewPage}</span>
            </div>

            {/* Preview Container */}
            <div 
              className="bg-[#1a1a1a] rounded-lg p-4 flex justify-center overflow-auto"
              style={{ minHeight: '600px' }}
            >
              <div
                className="bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  width: `${deviceSizes[previewDevice].width}px`,
                  height: `${deviceSizes[previewDevice].height}px`,
                  maxWidth: '100%'
                }}
              >
                {/* Device Frame */}
                <div className={`w-full h-full relative ${previewDevice === 'mobile' ? 'rounded-[20px]' : ''}`}>
                  {/* Browser Chrome for Desktop/Tablet */}
                  {previewDevice !== 'mobile' && (
                    <div className="bg-[#2a2a2a] px-4 py-2 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                      </div>
                      <div className="flex-1 bg-[#1a1a1a] rounded px-3 py-1 text-xs text-[#A1A1AA] truncate">
                        {SITE_URL}{previewPage}
                      </div>
                    </div>
                  )}
                  
                  {/* Mobile Notch */}
                  {previewDevice === 'mobile' && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-xl z-10" />
                  )}
                  
                  {/* iframe */}
                  <iframe
                    ref={iframeRef}
                    key={previewKey}
                    src={`${SITE_URL}${previewPage}`}
                    className="w-full border-0"
                    style={{ 
                      height: previewDevice === 'mobile' 
                        ? `${deviceSizes[previewDevice].height}px` 
                        : `${deviceSizes[previewDevice].height - 36}px`
                    }}
                    title="Website Preview"
                  />
                </div>
              </div>
            </div>

            {/* Quick Tips */}
            <div className="mt-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.15)] rounded">
              <p className="text-sm text-[#A1A1AA]">
                <span className="text-[#D4AF37] font-semibold">💡 Tip:</span> After making changes in other CMS sections, click the refresh button to see updates in the preview.
                Use the page selector to preview different pages on your site.
              </p>
            </div>
          </motion.div>
        )}

        {/* Header & Banner Section */}
        {activeSection === "header" && settings?.announcement_banner && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Announcement Banner
              </h2>
              <button
                onClick={() => handleSaveSection("announcement_banner")}
                disabled={saving.announcement_banner}
                className="btn-primary flex items-center gap-2"
              >
                {saving.announcement_banner ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Banner
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center gap-4">
                <label className="text-[#A1A1AA]">Show Banner:</label>
                <button
                  onClick={() => updateSetting("announcement_banner", "enabled", !settings.announcement_banner.enabled)}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    settings.announcement_banner.enabled ? 'bg-green-600' : 'bg-[#52525B]'
                  }`}
                >
                  <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                    settings.announcement_banner.enabled ? 'left-8' : 'left-1'
                  }`} />
                </button>
              </div>

              {/* Banner Text */}
              <div>
                <label className="form-label">Banner Text</label>
                <input
                  type="text"
                  value={settings.announcement_banner.text || ""}
                  onChange={(e) => updateSetting("announcement_banner", "text", e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Enter banner message..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Link */}
                <div>
                  <label className="form-label">Link (optional)</label>
                  <input
                    type="text"
                    value={settings.announcement_banner.link || ""}
                    onChange={(e) => updateSetting("announcement_banner", "link", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="/booking"
                  />
                </div>

                {/* Background Color */}
                <div>
                  <label className="form-label">Background Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={settings.announcement_banner.bg_color || "#D4AF37"}
                      onChange={(e) => updateSetting("announcement_banner", "bg_color", e.target.value)}
                      className="w-12 h-12 bg-transparent border border-[rgba(212,175,55,0.3)] cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.announcement_banner.bg_color || "#D4AF37"}
                      onChange={(e) => updateSetting("announcement_banner", "bg_color", e.target.value)}
                      className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              {settings.announcement_banner.enabled && (
                <div className="mt-4">
                  <label className="form-label mb-2">Preview:</label>
                  <div 
                    className="p-3 text-center text-sm font-medium"
                    style={{ backgroundColor: settings.announcement_banner.bg_color || "#D4AF37", color: "#050505" }}
                  >
                    {settings.announcement_banner.text || "Your banner text here"}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Navigation Menu Section */}
        {activeSection === "navigation" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Navigation Menu
                </h2>
                <p className="text-[#a1a1aa] text-sm">Customize your site's logo and navigation links</p>
              </div>
              <button
                onClick={() => handleSaveSection("header")}
                disabled={saving.header}
                className="btn-primary flex items-center gap-2"
              >
                {saving.header ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Navigation
              </button>
            </div>

            <div className="space-y-6">
              {/* Logo Settings */}
              <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#D4AF37] font-medium mb-4">Logo</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#a1a1aa] text-xs">Logo Text (Main)</label>
                    <input
                      type="text"
                      value={settings.header?.logo?.text || "MIX & MINGLE"}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          header: {
                            ...prev.header,
                            logo: { ...prev.header?.logo, text: e.target.value }
                          }
                        }));
                      }}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="MIX & MINGLE"
                    />
                  </div>
                  <div>
                    <label className="text-[#a1a1aa] text-xs">Logo Text (Highlight)</label>
                    <input
                      type="text"
                      value={settings.header?.logo?.highlight || "Events"}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          header: {
                            ...prev.header,
                            logo: { ...prev.header?.logo, highlight: e.target.value }
                          }
                        }));
                      }}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Events"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <ImageUpload
                    label="Logo Image (optional - replaces text logo)"
                    value={settings.header?.logo?.image || ""}
                    onChange={(url) => {
                      setSettings(prev => ({
                        ...prev,
                        header: {
                          ...prev.header,
                          logo: { ...prev.header?.logo, image: url }
                        }
                      }));
                    }}
                    folder="branding"
                    placeholder="Upload logo or enter URL"
                    previewHeight="h-20"
                  />
                  <p className="text-[#a1a1aa] text-xs mt-1">Upload PNG/JPEG logo image or leave empty to use text logo</p>
                </div>
                {/* Logo Preview */}
                <div className="mt-4 p-4 bg-[#121212] border border-[rgba(212,175,55,0.2)] rounded">
                  <p className="text-[#a1a1aa] text-xs mb-2">Preview:</p>
                  <div className="flex items-center gap-3">
                    {settings.header?.logo?.image ? (
                      <img 
                        src={settings.header.logo.image.startsWith('/uploads') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${settings.header.logo.image}` 
                          : settings.header.logo.image
                        } 
                        alt="Logo" 
                        className="h-10 w-auto"
                        onError={(e) => { 
                          const target = e.currentTarget;
                          if (target) target.style.display = 'none'; 
                        }}
                      />
                    ) : (
                      <span className="font-['Playfair_Display'] text-xl font-semibold">
                        <span className="text-[#F5F5F0]">{settings.header?.logo?.text || "MIX & MINGLE"} </span>
                        <span className="text-[#D4AF37]">{settings.header?.logo?.highlight || "Events"}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-[#D4AF37] font-medium">Navigation Links</h3>
                  <button
                    onClick={() => {
                      const newLinks = [...(settings.header?.nav_links || []), { label: "New Link", url: "/" }];
                      setSettings(prev => ({
                        ...prev,
                        header: { ...prev.header, nav_links: newLinks }
                      }));
                    }}
                    className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add Link
                  </button>
                </div>
                <div className="space-y-2">
                  {(settings.header?.nav_links || []).map((link, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={link.label || ""}
                        onChange={(e) => {
                          const newLinks = [...settings.header.nav_links];
                          newLinks[index] = { ...link, label: e.target.value };
                          setSettings(prev => ({
                            ...prev,
                            header: { ...prev.header, nav_links: newLinks }
                          }));
                        }}
                        className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Link Label"
                      />
                      <input
                        type="text"
                        value={link.url || ""}
                        onChange={(e) => {
                          const newLinks = [...settings.header.nav_links];
                          newLinks[index] = { ...link, url: e.target.value };
                          setSettings(prev => ({
                            ...prev,
                            header: { ...prev.header, nav_links: newLinks }
                          }));
                        }}
                        className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                        placeholder="/page-url"
                      />
                      <button
                        onClick={() => {
                          const newLinks = settings.header.nav_links.filter((_, i) => i !== index);
                          setSettings(prev => ({
                            ...prev,
                            header: { ...prev.header, nav_links: newLinks }
                          }));
                        }}
                        className="text-red-500 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Book Now Button */}
              <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#D4AF37] font-medium mb-4">Book Now Button</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#a1a1aa] text-xs">Button Text</label>
                    <input
                      type="text"
                      value={settings.header?.book_now?.label || "Book Now"}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          header: {
                            ...prev.header,
                            book_now: { ...prev.header?.book_now, label: e.target.value }
                          }
                        }));
                      }}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Book Now"
                    />
                  </div>
                  <div>
                    <label className="text-[#a1a1aa] text-xs">Button URL</label>
                    <input
                      type="text"
                      value={settings.header?.book_now?.url || "/booking"}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          header: {
                            ...prev.header,
                            book_now: { ...prev.header?.book_now, url: e.target.value }
                          }
                        }));
                      }}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="/booking"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Hero Section */}
        {activeSection === "hero" && settings?.hero && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Homepage Hero Section
              </h2>
              <button
                onClick={() => handleSaveSection("hero")}
                disabled={saving.hero}
                className="btn-primary flex items-center gap-2"
              >
                {saving.hero ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Hero
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="form-label">Main Title</label>
                <input
                  type="text"
                  value={settings.hero.title || ""}
                  onChange={(e) => updateSetting("hero", "title", e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none text-lg"
                  placeholder="Elevate Your Event Experience"
                />
              </div>

              <div>
                <label className="form-label">Subtitle</label>
                <textarea
                  value={settings.hero.subtitle || ""}
                  onChange={(e) => updateSetting("hero", "subtitle", e.target.value)}
                  rows={3}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                  placeholder="Premium mobile bar experiences..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Button Text</label>
                  <input
                    type="text"
                    value={settings.hero.button_text || ""}
                    onChange={(e) => updateSetting("hero", "button_text", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Book Your Experience"
                  />
                </div>
                <div>
                  <label className="form-label">Button Link</label>
                  <input
                    type="text"
                    value={settings.hero.button_link || ""}
                    onChange={(e) => updateSetting("hero", "button_link", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="/booking"
                  />
                </div>
              </div>

              <div>
                <ImageUpload
                  label="Background Image"
                  value={settings.hero.background_image || ""}
                  onChange={(url) => updateSetting("hero", "background_image", url)}
                  folder="hero"
                  placeholder="Upload hero background or enter URL"
                  previewHeight="h-40"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* About Section */}
        {activeSection === "about_section" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                About Section (Our Story)
              </h2>
              <button
                onClick={() => handleSaveSection("about_section")}
                disabled={saving.about_section}
                className="btn-primary flex items-center gap-2"
              >
                {saving.about_section ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save About Section
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="about-enabled"
                  checked={settings?.about_section?.enabled !== false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      about_section: {
                        ...prev.about_section,
                        enabled: e.target.checked
                      }
                    }));
                  }}
                  className="w-5 h-5 accent-[#D4AF37]"
                />
                <label htmlFor="about-enabled" className="text-[#F5F5F0]">Show About Section</label>
              </div>

              <div>
                <label className="form-label">Section Tagline</label>
                <input
                  type="text"
                  value={settings?.about_section?.tagline || "Our Story"}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      about_section: { ...prev.about_section, tagline: e.target.value }
                    }));
                  }}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Our Story"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Title Line 1</label>
                  <input
                    type="text"
                    value={settings?.about_section?.title_line1 || "Crafting Moments of"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        about_section: { ...prev.about_section, title_line1: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Crafting Moments of"
                  />
                </div>
                <div>
                  <label className="form-label">Title Line 2 (Gold Highlight)</label>
                  <input
                    type="text"
                    value={settings?.about_section?.title_line2 || "Pure Elegance"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        about_section: { ...prev.about_section, title_line2: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#D4AF37] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Pure Elegance"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Description Paragraph 1</label>
                <textarea
                  value={settings?.about_section?.description_1 || ""}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      about_section: { ...prev.about_section, description_1: e.target.value }
                    }));
                  }}
                  rows={3}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                  placeholder="At Mix & Mingle Events, we believe..."
                />
              </div>

              <div>
                <label className="form-label">Description Paragraph 2</label>
                <textarea
                  value={settings?.about_section?.description_2 || ""}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      about_section: { ...prev.about_section, description_2: e.target.value }
                    }));
                  }}
                  rows={3}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                  placeholder="From intimate gatherings to grand galas..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Button Text</label>
                  <input
                    type="text"
                    value={settings?.about_section?.button_text || "Explore Our Experiences"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        about_section: { ...prev.about_section, button_text: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Explore Our Experiences"
                  />
                </div>
                <div>
                  <label className="form-label">Button Link</label>
                  <input
                    type="text"
                    value={settings?.about_section?.button_link || "/experiences"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        about_section: { ...prev.about_section, button_link: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="/experiences"
                  />
                </div>
              </div>

              <div>
                <ImageUpload
                  label="Section Image"
                  value={settings?.about_section?.image_url || ""}
                  onChange={(url) => {
                    setSettings(prev => ({
                      ...prev,
                      about_section: { ...prev.about_section, image_url: url }
                    }));
                  }}
                  folder="about"
                  placeholder="Upload section image or enter URL"
                  previewHeight="h-40"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Badge Number (e.g., "10+")</label>
                  <input
                    type="text"
                    value={settings?.about_section?.badge_number || "10+"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        about_section: { ...prev.about_section, badge_number: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#D4AF37] p-3 focus:border-[#D4AF37] focus:outline-none text-xl font-bold"
                    placeholder="10+"
                  />
                </div>
                <div>
                  <label className="form-label">Badge Text</label>
                  <input
                    type="text"
                    value={settings?.about_section?.badge_text || "Years of Excellence"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        about_section: { ...prev.about_section, badge_text: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Years of Excellence"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Featured Section */}
        {activeSection === "featured_section" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Featured Experiences Section Header
              </h2>
              <button
                onClick={() => handleSaveSection("featured_section")}
                disabled={saving.featured_section}
                className="btn-primary flex items-center gap-2"
              >
                {saving.featured_section ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Featured Section
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="featured-enabled"
                  checked={settings?.featured_section?.enabled !== false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      featured_section: {
                        ...prev.featured_section,
                        enabled: e.target.checked
                      }
                    }));
                  }}
                  className="w-5 h-5 accent-[#D4AF37]"
                />
                <label htmlFor="featured-enabled" className="text-[#F5F5F0]">Show Featured Experiences Section</label>
              </div>

              <div>
                <label className="form-label">Section Tagline</label>
                <input
                  type="text"
                  value={settings?.featured_section?.tagline || "Our Services"}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      featured_section: { ...prev.featured_section, tagline: e.target.value }
                    }));
                  }}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Our Services"
                />
              </div>

              <div>
                <label className="form-label">Section Title</label>
                <input
                  type="text"
                  value={settings?.featured_section?.title || "Featured Experiences"}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      featured_section: { ...prev.featured_section, title: e.target.value }
                    }));
                  }}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none text-lg"
                  placeholder="Featured Experiences"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Button Text</label>
                  <input
                    type="text"
                    value={settings?.featured_section?.button_text || "View All Experiences"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        featured_section: { ...prev.featured_section, button_text: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="View All Experiences"
                  />
                </div>
                <div>
                  <label className="form-label">Button Link</label>
                  <input
                    type="text"
                    value={settings?.featured_section?.button_link || "/experiences"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        featured_section: { ...prev.featured_section, button_link: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="/experiences"
                  />
                </div>
              </div>

              <p className="text-[#A1A1AA] text-sm">
                Note: The actual experience packages displayed in this section are managed in the "Packages" section of the admin panel.
              </p>
            </div>
          </motion.div>
        )}

        {/* Testimonials Section Header */}
        {activeSection === "testimonials_section" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Testimonials Section Header
              </h2>
              <button
                onClick={() => handleSaveSection("testimonials_section")}
                disabled={saving.testimonials_section}
                className="btn-primary flex items-center gap-2"
              >
                {saving.testimonials_section ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Testimonials Header
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="testimonials-section-enabled"
                  checked={settings?.testimonials_section?.enabled !== false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      testimonials_section: {
                        ...prev.testimonials_section,
                        enabled: e.target.checked
                      }
                    }));
                  }}
                  className="w-5 h-5 accent-[#D4AF37]"
                />
                <label htmlFor="testimonials-section-enabled" className="text-[#F5F5F0]">Show Testimonials Section</label>
              </div>

              <div>
                <label className="form-label">Section Tagline</label>
                <input
                  type="text"
                  value={settings?.testimonials_section?.tagline || "Client Stories"}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      testimonials_section: { ...prev.testimonials_section, tagline: e.target.value }
                    }));
                  }}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Client Stories"
                />
              </div>

              <div>
                <label className="form-label">Section Title</label>
                <input
                  type="text"
                  value={settings?.testimonials_section?.title || "What Our Clients Say"}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      testimonials_section: { ...prev.testimonials_section, title: e.target.value }
                    }));
                  }}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none text-lg"
                  placeholder="What Our Clients Say"
                />
              </div>

              <p className="text-[#A1A1AA] text-sm">
                Note: The actual testimonial content is managed in the "Testimonials Content" tab.
              </p>
            </div>
          </motion.div>
        )}

        {/* CTA Section */}
        {activeSection === "cta_section" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Call-to-Action Section
              </h2>
              <button
                onClick={() => handleSaveSection("cta_section")}
                disabled={saving.cta_section}
                className="btn-primary flex items-center gap-2"
              >
                {saving.cta_section ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save CTA Section
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="cta-enabled"
                  checked={settings?.cta_section?.enabled !== false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      cta_section: {
                        ...prev.cta_section,
                        enabled: e.target.checked
                      }
                    }));
                  }}
                  className="w-5 h-5 accent-[#D4AF37]"
                />
                <label htmlFor="cta-enabled" className="text-[#F5F5F0]">Show CTA Section</label>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Title Line 1</label>
                  <input
                    type="text"
                    value={settings?.cta_section?.title_line1 || "Ready to Create Something"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        cta_section: { ...prev.cta_section, title_line1: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Ready to Create Something"
                  />
                </div>
                <div>
                  <label className="form-label">Title Highlight (Gold)</label>
                  <input
                    type="text"
                    value={settings?.cta_section?.title_highlight || "Extraordinary?"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        cta_section: { ...prev.cta_section, title_highlight: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#D4AF37] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Extraordinary?"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={settings?.cta_section?.description || ""}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      cta_section: { ...prev.cta_section, description: e.target.value }
                    }));
                  }}
                  rows={2}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                  placeholder="Let us craft an unforgettable mocktail experience..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Button Text</label>
                  <input
                    type="text"
                    value={settings?.cta_section?.button_text || "Start Planning"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        cta_section: { ...prev.cta_section, button_text: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Start Planning"
                  />
                </div>
                <div>
                  <label className="form-label">Button Link</label>
                  <input
                    type="text"
                    value={settings?.cta_section?.button_link || "/booking"}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        cta_section: { ...prev.cta_section, button_link: e.target.value }
                      }));
                    }}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="/booking"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}


        {/* Stats Section */}
        {activeSection === "stats" && settings?.stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Stats Section
              </h2>
              <button
                onClick={() => handleSaveSection("stats")}
                disabled={saving.stats}
                className="btn-primary flex items-center gap-2"
              >
                {saving.stats ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Stats
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {settings.stats.map((stat, index) => (
                <div key={index} className="flex gap-4 p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]">
                  <div className="flex-1">
                    <label className="text-[#a1a1aa] text-xs">Number</label>
                    <input
                      type="text"
                      value={stat.number || ""}
                      onChange={(e) => {
                        const newStats = [...settings.stats];
                        newStats[index] = { ...stat, number: e.target.value };
                        setSettings({ ...settings, stats: newStats });
                      }}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#D4AF37] p-2 font-bold text-lg focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-[#a1a1aa] text-xs">Label</label>
                    <input
                      type="text"
                      value={stat.label || ""}
                      onChange={(e) => {
                        const newStats = [...settings.stats];
                        newStats[index] = { ...stat, label: e.target.value };
                        setSettings({ ...settings, stats: newStats });
                      }}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Testimonials Section */}
        {activeSection === "testimonials" && settings?.testimonials && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Testimonials
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSettings({
                      ...settings,
                      testimonials: [...settings.testimonials, { quote: "", author: "", role: "", image: "" }]
                    });
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
                <button
                  onClick={() => handleSaveSection("testimonials")}
                  disabled={saving.testimonials}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving.testimonials ? (
                    <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {settings.testimonials.map((testimonial, index) => (
                <div key={index} className="p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-[#D4AF37] text-sm">Testimonial #{index + 1}</span>
                    {settings.testimonials.length > 1 && (
                      <button
                        onClick={() => {
                          setSettings({
                            ...settings,
                            testimonials: settings.testimonials.filter((_, i) => i !== index)
                          });
                        }}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-[#a1a1aa] text-xs">Quote</label>
                      <textarea
                        value={testimonial.quote || ""}
                        onChange={(e) => {
                          const newTestimonials = [...settings.testimonials];
                          newTestimonials[index] = { ...testimonial, quote: e.target.value };
                          setSettings({ ...settings, testimonials: newTestimonials });
                        }}
                        rows={2}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none resize-none"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[#a1a1aa] text-xs">Author Name</label>
                        <input
                          type="text"
                          value={testimonial.author || ""}
                          onChange={(e) => {
                            const newTestimonials = [...settings.testimonials];
                            newTestimonials[index] = { ...testimonial, author: e.target.value };
                            setSettings({ ...settings, testimonials: newTestimonials });
                          }}
                          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[#a1a1aa] text-xs">Role/Title</label>
                        <input
                          type="text"
                          value={testimonial.role || ""}
                          onChange={(e) => {
                            const newTestimonials = [...settings.testimonials];
                            newTestimonials[index] = { ...testimonial, role: e.target.value };
                            setSettings({ ...settings, testimonials: newTestimonials });
                          }}
                          className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <ImageUpload
                        label="Photo"
                        value={testimonial.image || ""}
                        onChange={(url) => {
                          const newTestimonials = [...settings.testimonials];
                          newTestimonials[index] = { ...testimonial, image: url };
                          setSettings({ ...settings, testimonials: newTestimonials });
                        }}
                        folder="testimonials"
                        placeholder="Upload photo or enter URL"
                        previewHeight="h-20"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* About Us Page Settings */}
        {activeSection === "about_page" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  About Us Page
                </h2>
                <p className="text-[#a1a1aa] text-sm mt-1">
                  Configure the full About Us page content • <a href="/about" target="_blank" className="text-[#D4AF37] hover:underline">Preview →</a>
                </p>
              </div>
              <button
                onClick={() => handleSaveSection("about_page")}
                disabled={saving.about_page}
                className="btn-primary flex items-center gap-2"
              >
                {saving.about_page ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save About Page
              </button>
            </div>

            <div className="space-y-8">
              {/* Hero Section */}
              <div className="border border-[rgba(212,175,55,0.1)] p-6 bg-[#0F0F0F]">
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Hero Section
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Page Title</label>
                    <input
                      type="text"
                      value={settings?.about_page?.hero?.title || ""}
                      onChange={(e) => updateNestedSetting("about_page", "hero", "title", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Our Story"
                    />
                  </div>
                  <div>
                    <label className="form-label">Subtitle</label>
                    <input
                      type="text"
                      value={settings?.about_page?.hero?.subtitle || ""}
                      onChange={(e) => updateNestedSetting("about_page", "hero", "subtitle", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Crafting Unforgettable Moments Since Day One"
                    />
                  </div>
                  <div>
                    <ImageUpload
                      label="Background Image"
                      value={settings?.about_page?.hero?.background_image || ""}
                      onChange={(url) => updateNestedSetting("about_page", "hero", "background_image", url)}
                      folder="about"
                      placeholder="Upload or enter image URL"
                      previewHeight="h-24"
                    />
                  </div>
                  <div>
                    <label className="form-label">Overlay Opacity (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={settings?.about_page?.hero?.overlay_opacity || 70}
                      onChange={(e) => updateNestedSetting("about_page", "hero", "overlay_opacity", parseInt(e.target.value))}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Our Story Section */}
              <div className="border border-[rgba(212,175,55,0.1)] p-6 bg-[#0F0F0F]">
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Our Story Section
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="form-label">Section Title</label>
                    <input
                      type="text"
                      value={settings?.about_page?.story?.title || ""}
                      onChange={(e) => updateNestedSetting("about_page", "story", "title", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="How It All Began"
                    />
                  </div>
                  <div>
                    <label className="form-label">Story Content (HTML)</label>
                    <RichTextEditor
                      content={settings?.about_page?.story?.content || ""}
                      onChange={(content) => updateNestedSetting("about_page", "story", "content", content)}
                      placeholder="Tell your story..."
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <ImageUpload
                        label="Story Image"
                        value={settings?.about_page?.story?.image || ""}
                        onChange={(url) => updateNestedSetting("about_page", "story", "image", url)}
                        folder="about"
                        placeholder="Upload or enter image URL"
                        previewHeight="h-40"
                      />
                    </div>
                    <div>
                      <label className="form-label">Image Position</label>
                      <select
                        value={settings?.about_page?.story?.image_position || "right"}
                        onChange={(e) => updateNestedSetting("about_page", "story", "image_position", e.target.value)}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      >
                        <option value="right">Right</option>
                        <option value="left">Left</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mission & Vision */}
              <div className="border border-[rgba(212,175,55,0.1)] p-6 bg-[#0F0F0F]">
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Mission & Vision
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-[#F5F5F0] font-medium">Mission</h4>
                    <input
                      type="text"
                      value={settings?.about_page?.mission?.title || ""}
                      onChange={(e) => updateNestedSetting("about_page", "mission", "title", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Our Mission"
                    />
                    <textarea
                      value={settings?.about_page?.mission?.content || ""}
                      onChange={(e) => updateNestedSetting("about_page", "mission", "content", e.target.value)}
                      rows={3}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                      placeholder="What drives you..."
                    />
                    <select
                      value={settings?.about_page?.mission?.icon || "target"}
                      onChange={(e) => updateNestedSetting("about_page", "mission", "icon", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    >
                      <option value="target">Target</option>
                      <option value="heart">Heart</option>
                      <option value="star">Star</option>
                      <option value="sparkles">Sparkles</option>
                    </select>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-[#F5F5F0] font-medium">Vision</h4>
                    <input
                      type="text"
                      value={settings?.about_page?.vision?.title || ""}
                      onChange={(e) => updateNestedSetting("about_page", "vision", "title", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Our Vision"
                    />
                    <textarea
                      value={settings?.about_page?.vision?.content || ""}
                      onChange={(e) => updateNestedSetting("about_page", "vision", "content", e.target.value)}
                      rows={3}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                      placeholder="Where you're going..."
                    />
                    <select
                      value={settings?.about_page?.vision?.icon || "eye"}
                      onChange={(e) => updateNestedSetting("about_page", "vision", "icon", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    >
                      <option value="eye">Eye</option>
                      <option value="lightbulb">Lightbulb</option>
                      <option value="star">Star</option>
                      <option value="sparkles">Sparkles</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Values Section */}
              <div className="border border-[rgba(212,175,55,0.1)] p-6 bg-[#0F0F0F]">
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Values Section
                </h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Section Title</label>
                      <input
                        type="text"
                        value={settings?.about_page?.values?.title || ""}
                        onChange={(e) => updateNestedSetting("about_page", "values", "title", e.target.value)}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                        placeholder="What Sets Us Apart"
                      />
                    </div>
                    <div>
                      <label className="form-label">Subtitle</label>
                      <input
                        type="text"
                        value={settings?.about_page?.values?.subtitle || ""}
                        onChange={(e) => updateNestedSetting("about_page", "values", "subtitle", e.target.value)}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                        placeholder="The principles that guide everything we do"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className="form-label mb-0">Value Items</label>
                      <button
                        onClick={() => {
                          const currentValues = settings?.about_page?.values?.items || [];
                          updateNestedSetting("about_page", "values", "items", [
                            ...currentValues,
                            { icon: "sparkles", title: "", description: "" }
                          ]);
                        }}
                        className="text-[#D4AF37] text-sm flex items-center gap-1 hover:underline"
                      >
                        <Plus className="w-4 h-4" /> Add Value
                      </button>
                    </div>
                    {(settings?.about_page?.values?.items || []).map((value, index) => (
                      <div key={index} className="grid md:grid-cols-4 gap-3 p-3 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
                        <select
                          value={value.icon || "sparkles"}
                          onChange={(e) => {
                            const items = [...(settings?.about_page?.values?.items || [])];
                            items[index] = { ...value, icon: e.target.value };
                            updateNestedSetting("about_page", "values", "items", items);
                          }}
                          className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2"
                        >
                          <option value="heart">Heart</option>
                          <option value="sparkles">Sparkles</option>
                          <option value="award">Award</option>
                          <option value="users">Users</option>
                          <option value="star">Star</option>
                          <option value="check">Check</option>
                        </select>
                        <input
                          type="text"
                          value={value.title || ""}
                          onChange={(e) => {
                            const items = [...(settings?.about_page?.values?.items || [])];
                            items[index] = { ...value, title: e.target.value };
                            updateNestedSetting("about_page", "values", "items", items);
                          }}
                          className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2"
                          placeholder="Title"
                        />
                        <input
                          type="text"
                          value={value.description || ""}
                          onChange={(e) => {
                            const items = [...(settings?.about_page?.values?.items || [])];
                            items[index] = { ...value, description: e.target.value };
                            updateNestedSetting("about_page", "values", "items", items);
                          }}
                          className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2"
                          placeholder="Description"
                        />
                        <button
                          onClick={() => {
                            const items = (settings?.about_page?.values?.items || []).filter((_, i) => i !== index);
                            updateNestedSetting("about_page", "values", "items", items);
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="border border-[rgba(212,175,55,0.1)] p-6 bg-[#0F0F0F]">
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Statistics
                </h3>
                <div className="space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings?.about_page?.stats?.show !== false}
                      onChange={(e) => updateNestedSetting("about_page", "stats", "show", e.target.checked)}
                      className="w-5 h-5"
                    />
                    <span className="text-[#F5F5F0]">Show Statistics Section</span>
                  </label>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(settings?.about_page?.stats?.items || []).map((stat, index) => (
                      <div key={index} className="p-3 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
                        <input
                          type="text"
                          value={stat.number || ""}
                          onChange={(e) => {
                            const items = [...(settings?.about_page?.stats?.items || [])];
                            items[index] = { ...stat, number: e.target.value };
                            updateNestedSetting("about_page", "stats", "items", items);
                          }}
                          className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#D4AF37] p-2 text-2xl font-bold text-center mb-2"
                          placeholder="500+"
                        />
                        <input
                          type="text"
                          value={stat.label || ""}
                          onChange={(e) => {
                            const items = [...(settings?.about_page?.stats?.items || [])];
                            items[index] = { ...stat, label: e.target.value };
                            updateNestedSetting("about_page", "stats", "items", items);
                          }}
                          className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm text-center"
                          placeholder="Events Hosted"
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const items = [...(settings?.about_page?.stats?.items || []), { number: "", label: "" }];
                      updateNestedSetting("about_page", "stats", "items", items);
                    }}
                    className="text-[#D4AF37] text-sm flex items-center gap-1 hover:underline"
                  >
                    <Plus className="w-4 h-4" /> Add Stat
                  </button>
                </div>
              </div>

              {/* Testimonial Highlight */}
              <div className="border border-[rgba(212,175,55,0.1)] p-6 bg-[#0F0F0F]">
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Featured Testimonial
                </h3>
                <div className="space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings?.about_page?.testimonial_highlight?.show !== false}
                      onChange={(e) => updateNestedSetting("about_page", "testimonial_highlight", "show", e.target.checked)}
                      className="w-5 h-5"
                    />
                    <span className="text-[#F5F5F0]">Show Featured Testimonial</span>
                  </label>
                  <textarea
                    value={settings?.about_page?.testimonial_highlight?.quote || ""}
                    onChange={(e) => updateNestedSetting("about_page", "testimonial_highlight", "quote", e.target.value)}
                    rows={3}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none italic"
                    placeholder="Their testimonial quote..."
                  />
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={settings?.about_page?.testimonial_highlight?.author || ""}
                      onChange={(e) => updateNestedSetting("about_page", "testimonial_highlight", "author", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Author Name"
                    />
                    <input
                      type="text"
                      value={settings?.about_page?.testimonial_highlight?.role || ""}
                      onChange={(e) => updateNestedSetting("about_page", "testimonial_highlight", "role", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Role / Company"
                    />
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="border border-[rgba(212,175,55,0.1)] p-6 bg-[#0F0F0F]">
                <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Call to Action
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">CTA Title</label>
                    <input
                      type="text"
                      value={settings?.about_page?.cta?.title || ""}
                      onChange={(e) => updateNestedSetting("about_page", "cta", "title", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Ready to Create Something Amazing?"
                    />
                  </div>
                  <div>
                    <label className="form-label">Subtitle</label>
                    <input
                      type="text"
                      value={settings?.about_page?.cta?.subtitle || ""}
                      onChange={(e) => updateNestedSetting("about_page", "cta", "subtitle", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Let's bring your vision to life"
                    />
                  </div>
                  <div>
                    <label className="form-label">Button Text</label>
                    <input
                      type="text"
                      value={settings?.about_page?.cta?.button_text || ""}
                      onChange={(e) => updateNestedSetting("about_page", "cta", "button_text", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="Start Planning"
                    />
                  </div>
                  <div>
                    <label className="form-label">Button Link</label>
                    <input
                      type="text"
                      value={settings?.about_page?.cta?.button_link || ""}
                      onChange={(e) => updateNestedSetting("about_page", "cta", "button_link", e.target.value)}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="/contact"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Checkout Page Settings */}
        {activeSection === "checkout" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Checkout Page Settings
                </h2>
                <p className="text-[#a1a1aa] text-sm">Customize trust elements and checkout experience</p>
              </div>
              <button
                onClick={() => handleSaveSection("checkout")}
                disabled={saving.checkout}
                className="btn-primary flex items-center gap-2"
              >
                {saving.checkout ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Checkout Settings
              </button>
            </div>

            <div className="space-y-6">
              {/* Trust Badges */}
              <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-[#D4AF37] font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Trust Badges
                  </h3>
                  <button
                    onClick={() => {
                      const newBadges = [...(settings.checkout?.trust_badges || []), { icon: "shield", text: "New Badge" }];
                      setSettings(prev => ({
                        ...prev,
                        checkout: { ...prev.checkout, trust_badges: newBadges }
                      }));
                    }}
                    className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add Badge
                  </button>
                </div>
                <div className="space-y-2">
                  {(settings.checkout?.trust_badges || []).map((badge, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <select
                        value={badge.icon || "shield"}
                        onChange={(e) => {
                          const newBadges = [...settings.checkout.trust_badges];
                          newBadges[index] = { ...badge, icon: e.target.value };
                          setSettings(prev => ({
                            ...prev,
                            checkout: { ...prev.checkout, trust_badges: newBadges }
                          }));
                        }}
                        className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm"
                      >
                        <option value="shield">🛡️ Shield</option>
                        <option value="lock">🔒 Lock</option>
                        <option value="credit-card">💳 Card</option>
                        <option value="check">✓ Check</option>
                      </select>
                      <input
                        type="text"
                        value={badge.text || ""}
                        onChange={(e) => {
                          const newBadges = [...settings.checkout.trust_badges];
                          newBadges[index] = { ...badge, text: e.target.value };
                          setSettings(prev => ({
                            ...prev,
                            checkout: { ...prev.checkout, trust_badges: newBadges }
                          }));
                        }}
                        className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Badge text"
                      />
                      <button
                        onClick={() => {
                          const newBadges = settings.checkout.trust_badges.filter((_, i) => i !== index);
                          setSettings(prev => ({
                            ...prev,
                            checkout: { ...prev.checkout, trust_badges: newBadges }
                          }));
                        }}
                        className="text-red-500 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Guarantee & Support */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Guarantee Text</label>
                  <input
                    type="text"
                    value={settings.checkout?.guarantee_text || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      checkout: { ...prev.checkout, guarantee_text: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="100% Satisfaction Guarantee"
                  />
                </div>
                <div>
                  <label className="form-label">Support Text</label>
                  <input
                    type="text"
                    value={settings.checkout?.support_text || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      checkout: { ...prev.checkout, support_text: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Questions? Call us at (780) 555-0123"
                  />
                </div>
              </div>

              {/* Money Back Guarantee */}
              <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.checkout?.show_money_back || false}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        checkout: { ...prev.checkout, show_money_back: e.target.checked }
                      }))}
                      className="w-5 h-5 rounded border-[#D4AF37] bg-[#0F0F0F] text-[#D4AF37]"
                    />
                    <span className="text-[#F5F5F0]">Show Money-Back Guarantee</span>
                  </label>
                </div>
                {settings.checkout?.show_money_back && (
                  <div className="flex items-center gap-2">
                    <span className="text-[#A1A1AA]">Refund within</span>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={settings.checkout?.money_back_days || 7}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        checkout: { ...prev.checkout, money_back_days: parseInt(e.target.value) }
                      }))}
                      className="w-20 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-center focus:border-[#D4AF37] focus:outline-none"
                    />
                    <span className="text-[#A1A1AA]">days</span>
                  </div>
                )}
              </div>

              {/* Payment Methods */}
              <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#D4AF37] font-medium mb-4 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Accepted Payment Methods
                </h3>
                <p className="text-[#a1a1aa] text-xs mb-4">Select which payment methods to display on checkout</p>
                <div className="flex flex-wrap gap-4">
                  {['visa', 'mastercard', 'amex', 'discover', 'paypal', 'interac'].map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(settings.checkout?.payment_methods || []).includes(method)}
                        onChange={(e) => {
                          const currentMethods = settings.checkout?.payment_methods || [];
                          let newMethods;
                          if (e.target.checked) {
                            newMethods = [...currentMethods, method];
                          } else {
                            newMethods = currentMethods.filter(m => m !== method);
                          }
                          setSettings(prev => ({
                            ...prev,
                            checkout: { ...prev.checkout, payment_methods: newMethods }
                          }));
                        }}
                        className="w-4 h-4 rounded border-[#D4AF37] bg-[#0F0F0F] text-[#D4AF37]"
                      />
                      <span className="text-[#F5F5F0] capitalize">{method === 'amex' ? 'American Express' : method}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Terms & Confirmation */}
              <div className="space-y-4">
                <div>
                  <label className="form-label">Terms Text (shown before checkout)</label>
                  <textarea
                    value={settings.checkout?.terms_text || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      checkout: { ...prev.checkout, terms_text: e.target.value }
                    }))}
                    rows={2}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                    placeholder="By completing this purchase, you agree to our Terms of Service..."
                  />
                </div>
                <div>
                  <label className="form-label">Confirmation Email Note</label>
                  <input
                    type="text"
                    value={settings.checkout?.confirmation_email_note || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      checkout: { ...prev.checkout, confirmation_email_note: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="A confirmation email will be sent to your email address."
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#D4AF37] font-medium mb-4">Checkout Preview</h3>
                <div className="bg-[#121212] p-6 rounded border border-[rgba(212,175,55,0.2)]">
                  {/* Trust Badges Preview */}
                  <div className="flex flex-wrap justify-center gap-6 mb-4 pb-4 border-b border-[rgba(212,175,55,0.1)]">
                    {(settings.checkout?.trust_badges || []).map((badge, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                        {badge.icon === 'shield' && <Shield className="w-4 h-4 text-green-500" />}
                        {badge.icon === 'lock' && <Lock className="w-4 h-4 text-green-500" />}
                        {badge.icon === 'credit-card' && <CreditCard className="w-4 h-4 text-green-500" />}
                        {badge.icon === 'check' && <Check className="w-4 h-4 text-green-500" />}
                        {badge.text}
                      </div>
                    ))}
                  </div>
                  {/* Guarantee */}
                  {settings.checkout?.guarantee_text && (
                    <p className="text-center text-[#D4AF37] font-medium mb-2">
                      {settings.checkout.guarantee_text}
                    </p>
                  )}
                  {settings.checkout?.show_money_back && (
                    <p className="text-center text-[#A1A1AA] text-sm mb-4">
                      {settings.checkout.money_back_days}-Day Money-Back Guarantee
                    </p>
                  )}
                  {/* Support */}
                  {settings.checkout?.support_text && (
                    <p className="text-center text-[#a1a1aa] text-xs">
                      {settings.checkout.support_text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Ticket Status Colors Section */}
        {activeSection === "ticket_colors" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Ticket Status Colors
                </h2>
                <p className="text-[#a1a1aa] text-sm">Customize colors for ticket availability statuses shown on event pages</p>
              </div>
              <button
                onClick={() => handleSaveSection("ticket_status_colors")}
                disabled={saving.ticket_status_colors}
                className="btn-primary flex items-center gap-2"
              >
                {saving.ticket_status_colors ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Colors
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { key: 'available', label: 'Available', default: '#22C55E' },
                { key: 'limited', label: 'Limited', default: '#F59E0B' },
                { key: 'sold_out', label: 'Sold Out', default: '#EF4444' },
                { key: 'coming_soon', label: 'Coming Soon', default: '#3B82F6' }
              ].map((status) => (
                <div key={status.key} className="p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]">
                  <label className="text-[#A1A1AA] block mb-2">{status.label}</label>
                  <div className="flex gap-3 items-center">
                    <input
                      type="color"
                      value={settings.ticket_status_colors?.[status.key] || status.default}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        ticket_status_colors: {
                          ...prev.ticket_status_colors,
                          [status.key]: e.target.value
                        }
                      }))}
                      className="w-14 h-10 bg-transparent border border-[rgba(212,175,55,0.3)] cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.ticket_status_colors?.[status.key] || status.default}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        ticket_status_colors: {
                          ...prev.ticket_status_colors,
                          [status.key]: e.target.value
                        }
                      }))}
                      className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                    />
                    <div 
                      className="px-4 py-2 text-white text-sm font-medium uppercase"
                      style={{ backgroundColor: settings.ticket_status_colors?.[status.key] || status.default }}
                    >
                      {status.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Contact Page Section */}
        {activeSection === "contact_page" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Contact Page
                </h2>
                <p className="text-[#a1a1aa] text-sm">Edit contact page content including map embed</p>
              </div>
              <button
                onClick={() => handleSaveSection("contact_page")}
                disabled={saving.contact_page}
                className="btn-primary flex items-center gap-2"
              >
                {saving.contact_page ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Contact Page
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Page Title</label>
                  <input
                    type="text"
                    value={settings.contact_page?.title || "Get in Touch"}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, title: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="form-label">Subtitle</label>
                  <input
                    type="text"
                    value={settings.contact_page?.subtitle || "Ready to elevate your next event?"}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, subtitle: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={settings.contact_page?.description || ""}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    contact_page: { ...prev.contact_page, description: e.target.value }
                  }))}
                  rows={3}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    value={settings.contact_page?.phone || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, phone: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={settings.contact_page?.email || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, email: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    value={settings.contact_page?.address || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, address: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="form-label">Business Hours</label>
                  <input
                    type="text"
                    value={settings.contact_page?.hours || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, hours: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              {/* Map Settings */}
              <div className="p-4 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]">
                <h3 className="text-[#D4AF37] font-medium mb-4 flex items-center gap-2">
                  <Map className="w-4 h-4" /> Map Settings
                </h3>
                
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.contact_page?.map_enabled !== false}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        contact_page: { ...prev.contact_page, map_enabled: e.target.checked }
                      }))}
                      className="w-5 h-5 rounded border-[#D4AF37] bg-[#0F0F0F] text-[#D4AF37]"
                    />
                    <span className="text-[#F5F5F0]">Show Map</span>
                  </label>
                </div>

                <div>
                  <label className="form-label">Google Maps Embed URL</label>
                  <p className="text-[#a1a1aa] text-xs mb-2">Go to Google Maps → Share → Embed a map → Copy the src URL</p>
                  <input
                    type="text"
                    value={settings.contact_page?.map_embed_url || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, map_embed_url: e.target.value }
                    }))}
                    placeholder="https://www.google.com/maps/embed?pb=..."
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>

                <div className="mt-4">
                  <label className="form-label">Map Height (px)</label>
                  <input
                    type="number"
                    value={settings.contact_page?.map_height || 400}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      contact_page: { ...prev.contact_page, map_height: e.target.value }
                    }))}
                    className="w-32 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Privacy Policy Section */}
        {activeSection === "privacy_policy" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Privacy Policy
                </h2>
                <p className="text-[#a1a1aa] text-sm">Edit your privacy policy page content (available at /privacy)</p>
              </div>
              <button
                onClick={() => handleSaveSection("privacy_policy")}
                disabled={saving.privacy_policy}
                className="btn-primary flex items-center gap-2"
              >
                {saving.privacy_policy ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Privacy Policy
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Page Title</label>
                  <input
                    type="text"
                    value={settings.privacy_policy?.title || "Privacy Policy"}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      privacy_policy: { ...prev.privacy_policy, title: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="form-label">Last Updated Date</label>
                  <input
                    type="date"
                    value={settings.privacy_policy?.last_updated || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      privacy_policy: { ...prev.privacy_policy, last_updated: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Policy Content (Rich Text)</label>
                <p className="text-[#a1a1aa] text-xs mb-2">Use headings (H2, H3), lists, and formatting to structure your policy</p>
                <RichTextEditor
                  content={settings.privacy_policy?.content || ""}
                  onChange={(html) => setSettings(prev => ({
                    ...prev,
                    privacy_policy: { ...prev.privacy_policy, content: html }
                  }))}
                  placeholder="Enter your privacy policy content..."
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Terms of Service Section */}
        {activeSection === "terms_of_service" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Terms of Service
                </h2>
                <p className="text-[#a1a1aa] text-sm">Edit your terms of service page content (available at /terms)</p>
              </div>
              <button
                onClick={() => handleSaveSection("terms_of_service")}
                disabled={saving.terms_of_service}
                className="btn-primary flex items-center gap-2"
              >
                {saving.terms_of_service ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Terms
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Page Title</label>
                  <input
                    type="text"
                    value={settings.terms_of_service?.title || "Terms of Service"}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      terms_of_service: { ...prev.terms_of_service, title: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="form-label">Last Updated Date</label>
                  <input
                    type="date"
                    value={settings.terms_of_service?.last_updated || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      terms_of_service: { ...prev.terms_of_service, last_updated: e.target.value }
                    }))}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Terms Content (Rich Text)</label>
                <p className="text-[#a1a1aa] text-xs mb-2">Use headings (H2, H3), lists, and formatting to structure your terms</p>
                <RichTextEditor
                  content={settings.terms_of_service?.content || ""}
                  onChange={(html) => setSettings(prev => ({
                    ...prev,
                    terms_of_service: { ...prev.terms_of_service, content: html }
                  }))}
                  placeholder="Enter your terms of service content..."
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Quick Links Section */}
        {activeSection === "quick_links" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Quick Links
                </h2>
                <p className="text-[#a1a1aa] text-sm">Edit the quick links shown in the footer</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const newLinks = [...(settings.quick_links || []), { label: "New Link", url: "/" }];
                    setSettings({ ...settings, quick_links: newLinks });
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Link
                </button>
                <button
                  onClick={() => handleSaveSection("quick_links")}
                  disabled={saving.quick_links}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving.quick_links ? (
                    <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {(settings.quick_links || []).map((link, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]">
                  <div className="flex-1 grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[#a1a1aa] text-xs">Label</label>
                      <input
                        type="text"
                        value={link.label || ""}
                        onChange={(e) => {
                          const newLinks = [...settings.quick_links];
                          newLinks[index] = { ...link, label: e.target.value };
                          setSettings({ ...settings, quick_links: newLinks });
                        }}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[#a1a1aa] text-xs">URL</label>
                      <input
                        type="text"
                        value={link.url || ""}
                        onChange={(e) => {
                          const newLinks = [...settings.quick_links];
                          newLinks[index] = { ...link, url: e.target.value };
                          setSettings({ ...settings, quick_links: newLinks });
                        }}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSettings({
                        ...settings,
                        quick_links: settings.quick_links.filter((_, i) => i !== index)
                      });
                    }}
                    className="text-red-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Services Section */}
        {activeSection === "services" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                  Services
                </h2>
                <p className="text-[#a1a1aa] text-sm">Edit the services list shown in the footer</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const newServices = [...(settings.services || []), "New Service"];
                    setSettings({ ...settings, services: newServices });
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Service
                </button>
                <button
                  onClick={() => handleSaveSection("services")}
                  disabled={saving.services}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving.services ? (
                    <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {(settings.services || []).map((service, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-[#0F0F0F] border border-[rgba(212,175,55,0.1)]">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={service || ""}
                      onChange={(e) => {
                        const newServices = [...settings.services];
                        newServices[index] = e.target.value;
                        setSettings({ ...settings, services: newServices });
                      }}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setSettings({
                        ...settings,
                        services: settings.services.filter((_, i) => i !== index)
                      });
                    }}
                    className="text-red-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Section */}
        {activeSection === "footer" && settings?.footer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                Footer Settings
              </h2>
              <button
                onClick={() => handleSaveSection("footer")}
                disabled={saving.footer}
                className="btn-primary flex items-center gap-2"
              >
                {saving.footer ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Footer
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="form-label">Tagline</label>
                <input
                  type="text"
                  value={settings.footer.tagline || ""}
                  onChange={(e) => updateSetting("footer", "tagline", e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Crafting unforgettable moments..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#D4AF37]" />
                    Phone
                  </label>
                  <input
                    type="text"
                    value={settings.footer.phone || ""}
                    onChange={(e) => updateSetting("footer", "phone", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#D4AF37]" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={settings.footer.email || ""}
                    onChange={(e) => updateSetting("footer", "email", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="form-label flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#22C55E]" />
                  WhatsApp Number (international format, digits only)
                </label>
                <input
                  type="text"
                  value={settings.footer.whatsapp || ""}
                  onChange={(e) => updateSetting("footer", "whatsapp", e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  placeholder="e.g. 17808632906"
                  data-testid="footer-whatsapp-input"
                />
                <p className="text-[#a1a1aa] text-xs mt-1">Will render a "Chat on WhatsApp" link in the footer and Contact page. Use country code without + or spaces.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" />
                    Address
                  </label>
                  <input
                    type="text"
                    value={settings.footer.address || ""}
                    onChange={(e) => updateSetting("footer", "address", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#D4AF37]" />
                    Business Hours
                  </label>
                  <input
                    type="text"
                    value={settings.footer.hours || ""}
                    onChange={(e) => updateSetting("footer", "hours", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>

              {/* Social Links */}
              <div>
                <label className="form-label mb-3">Social Links</label>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[#a1a1aa] text-xs">Instagram</label>
                    <input
                      type="text"
                      value={settings.footer.social_links?.instagram || ""}
                      onChange={(e) => updateNestedSetting("footer", "social_links", "instagram", e.target.value)}
                      className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <label className="text-[#a1a1aa] text-xs">Facebook</label>
                    <input
                      type="text"
                      value={settings.footer.social_links?.facebook || ""}
                      onChange={(e) => updateNestedSetting("footer", "social_links", "facebook", e.target.value)}
                      className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <label className="text-[#a1a1aa] text-xs">TikTok</label>
                    <input
                      type="text"
                      value={settings.footer.social_links?.tiktok || ""}
                      onChange={(e) => updateNestedSetting("footer", "social_links", "tiktok", e.target.value)}
                      className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                      placeholder="https://tiktok.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Copyright & Policy Links */}
              <div className="border-t border-[rgba(212,175,55,0.1)] pt-6 mt-6">
                <label className="form-label mb-3">Bottom Bar (Copyright & Policy Links)</label>
                
                <div className="mb-4">
                  <label className="text-[#a1a1aa] text-xs">Copyright Text</label>
                  <input
                    type="text"
                    value={settings.footer.copyright || ""}
                    onChange={(e) => updateSetting("footer", "copyright", e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-2 focus:border-[#D4AF37] focus:outline-none"
                    placeholder="© 2026 MIX & MINGLE Events. All rights reserved."
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-[#a1a1aa] text-xs">Policy Links</label>
                    <button
                      type="button"
                      onClick={() => {
                        const newLinks = [...(settings.footer.policy_links || []), { label: "New Link", url: "/" }];
                        updateSetting("footer", "policy_links", newLinks);
                      }}
                      className="text-[#D4AF37] text-xs hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Link
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(settings.footer.policy_links || []).map((link, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={link.label || ""}
                          onChange={(e) => {
                            const newLinks = [...settings.footer.policy_links];
                            newLinks[index] = { ...link, label: e.target.value };
                            updateSetting("footer", "policy_links", newLinks);
                          }}
                          className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                          placeholder="Link Label"
                        />
                        <input
                          type="text"
                          value={link.url || ""}
                          onChange={(e) => {
                            const newLinks = [...settings.footer.policy_links];
                            newLinks[index] = { ...link, url: e.target.value };
                            updateSetting("footer", "policy_links", newLinks);
                          }}
                          className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                          placeholder="/privacy"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newLinks = settings.footer.policy_links.filter((_, i) => i !== index);
                            updateSetting("footer", "policy_links", newLinks);
                          }}
                          className="text-red-500 hover:text-red-400 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminWebsitePage;
